import React, { useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useFetch } from '../../hooks/useFetch';
import { adminAPI } from '../../api/client';
import { StatCard, Card, Badge, Spinner, fmt, fmtDate } from '../../components/UI';

export default function AdminDashboard() {
  const fetchStats = useCallback(() => adminAPI.stats(), []);
  const { data, loading, error } = useFetch(fetchStats);

  if (loading) return <div style={{ padding: 32 }}><Spinner /></div>;
  if (error)   return <div style={{ padding: 32, color: '#dc2626' }}>{error}</div>;

  const { stats, monthly_payouts = [] } = data || {};
  const maxMonth = Math.max(...monthly_payouts.map(m => parseFloat(m.total)), 1);

  return (
    <div style={styles.wrap}>
      <div style={styles.header}>
        <div>
          <h1 style={styles.h1}>Admin Overview</h1>
          <p style={styles.sub}>Platform-wide stats, user activity, and pending actions.</p>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <Link to="/admin/withdrawals" style={styles.actionBtn}>
            Review Withdrawals {stats?.pending_withdrawals > 0 && <span style={styles.badge}>{stats.pending_withdrawals}</span>}
          </Link>
          <Link to="/admin/platform-accounts" style={{ ...styles.actionBtn, background: '#4f6ef7', color: '#fff', border: 'none' }}>
            Platform Accounts
          </Link>
        </div>
      </div>

      {/* Stat grid */}
      <div style={styles.statGrid} className="admin-stat-grid">
        <StatCard label="Total Users"          value={Number(stats?.total_users || 0).toLocaleString()}   icon="👥" color="#4f6ef7"  sub={`${stats?.active_users} active`} />
        <StatCard label="Total Platform Earned" value={fmt(stats?.total_platform_earned)}                 icon="💰" color="#16a34a" sub="Across all platforms" />
        <StatCard label="Total Paid Out"        value={fmt(stats?.total_paid_out)}                        icon="✅" color="#f4a61d" sub="Successfully withdrawn" />
        <StatCard label="Pending Withdrawals"   value={Number(stats?.pending_withdrawals || 0).toLocaleString()} icon="⏳" color="#dc2626" sub={fmt(stats?.pending_withdrawal_amount)} />
      </div>

      <div style={styles.grid2} className="admin-grid-2">
        {/* Monthly payout chart */}
        <Card title="Monthly Payout Volume">
          {monthly_payouts.length === 0
            ? <p style={{ color: '#9099b4', fontSize: 14 }}>No payout data yet.</p>
            : (
              <div style={{ display: 'flex', alignItems: 'flex-end', gap: 8, height: 120, paddingTop: 8 }}>
                {monthly_payouts.map(m => {
                  const h = Math.max(8, (parseFloat(m.total) / maxMonth) * 100);
                  return (
                    <div key={m.month} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6 }}>
                      <div style={{ fontSize: 10, color: '#9099b4', fontWeight: 600 }}>{fmt(m.total).replace('$','$').replace('.00','')}</div>
                      <div title={fmt(m.total)} style={{ width: '100%', height: `${h}%`, background: '#eef1ff', borderRadius: '4px 4px 0 0', borderBottom: '2px solid #4f6ef7', transition: 'all .3s', cursor: 'default' }}
                        onMouseEnter={e => e.target.style.background = '#4f6ef7'}
                        onMouseLeave={e => e.target.style.background = '#eef1ff'} />
                      <div style={{ fontSize: 11, color: '#9099b4' }}>{m.month}</div>
                    </div>
                  );
                })}
              </div>
            )
          }
        </Card>

        {/* Quick links */}
        <Card title="Quick Actions">
          {[
            { to: '/admin/users',            icon: '👥', label: 'Manage Users',            desc: 'View, edit, suspend accounts' },
            { to: '/admin/earnings',         icon: '💰', label: 'Add / Edit Earnings',     desc: 'Manually credit user earnings' },
            { to: '/admin/withdrawals',      icon: '🏦', label: 'Process Withdrawals',     desc: 'Approve or reject requests' },
            { to: '/admin/platform-accounts',icon: '🔐', label: 'Platform Credentials',   desc: 'View submitted account details' },
            { to: '/admin/activity',         icon: '🕐', label: 'Activity Logs',           desc: 'Full platform event log' },
          ].map(item => (
            <Link key={item.to} to={item.to} style={styles.quickLink}>
              <div style={styles.quickIcon}>{item.icon}</div>
              <div>
                <div style={{ fontSize: 14, fontWeight: 600, color: '#1a1f5e' }}>{item.label}</div>
                <div style={{ fontSize: 12, color: '#9099b4' }}>{item.desc}</div>
              </div>
              <span style={{ marginLeft: 'auto', color: '#c8cedf', fontSize: 16 }}>›</span>
            </Link>
          ))}
        </Card>
      </div>
    </div>
  );
}

const styles = {
  wrap:       { padding: '32px', fontFamily: "'DM Sans',sans-serif" },
  header:     { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 28, flexWrap: 'wrap', gap: 16 },
  h1:         { fontFamily: "'Sora',sans-serif", fontSize: 22, fontWeight: 700, color: '#1a1f5e', margin: 0 },
  sub:        { fontSize: 14, color: '#9099b4', marginTop: 4 },
  actionBtn:  { padding: '9px 18px', borderRadius: 10, background: '#fff', border: '1.5px solid #e2e6f0', fontSize: 13, fontWeight: 600, color: '#1a1f5e', textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: 8 },
  badge:      { background: '#dc2626', color: '#fff', borderRadius: 20, padding: '1px 7px', fontSize: 11, fontWeight: 700 },
  statGrid:   { display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 18, marginBottom: 24 },
  grid2:      { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 22 },
  quickLink:  { display: 'flex', alignItems: 'center', gap: 12, padding: '12px 0', borderBottom: '1px solid #f0f2f8', textDecoration: 'none', transition: 'opacity .15s' },
  quickIcon:  { width: 38, height: 38, borderRadius: 10, background: '#f0f2f8', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16, flexShrink: 0 },
};
